import { useEffect, useState } from 'react';

/**
 * outbox — saves that are waiting for the network.
 *
 * When a write (POST/PATCH/PUT/DELETE) cannot reach the server, ui/api.js
 * does NOT fail it. The request stays pending — the form stays open, its
 * button keeps spinning, nothing typed is lost — and it is listed here
 * until the connection returns and it goes through. It carries the same
 * X-Request-Id on every attempt, so the server never applies it twice
 * (api/src/lib/idempotency.js).
 *
 * Also tracks when a screen is showing a saved copy (snapshotStore)
 * instead of live data, for the offline banner.
 */

const items = new Map();
const listeners = new Set();
let sentAfterWait = 0;         // saves that went out after waiting, since last read
let snapshotAt = null;         // oldest saved copy shown while offline
const publish = () => listeners.forEach((fn) => { try { fn(); } catch { /* ignore */ } });

// What the user was doing, in their words, for the pending list.
const LABELS = [
  [/^POST \/payments/, 'قبض قسط'],
  [/^POST \/attendance/, 'حفظ التفقّد'],
  [/^PATCH \/attendance/, 'تعديل حالة حضور'],
  [/^POST \/students\/transfer/, 'نقل طالب'],
  [/^POST \/students/, 'إضافة طالب'],
  [/^PATCH \/students\/\d+\/family/, 'ربط طالب بعائلة'],
  [/^PATCH \/students\/\d+\/transportation/, 'تعديل مواصلات طالب'],
  [/^PATCH \/students/, 'تعديل بيانات طالب'],
  [/^DELETE \/students/, 'أرشفة طالب'],
  [/^POST \/families/, 'إضافة عائلة'],
  [/^PATCH \/families/, 'تعديل بيانات عائلة'],
  [/^POST \/teachers/, 'إضافة معلم'],
  [/^PATCH \/teachers/, 'تعديل بيانات معلم'],
  [/^POST \/exams\/\d+\/results/, 'حفظ علامات'],
  [/^PATCH \/exams\/\d+\/results/, 'تعديل علامة'],
  [/^POST \/exams\/\d+\/publish/, 'نشر امتحان'],
  [/^POST \/exams/, 'إضافة امتحان'],
  [/^PATCH \/exams/, 'تعديل امتحان'],
  [/^POST \/enrollments/, 'قيد طالب'],
  [/^POST \/notifications/, 'إرسال إشعار'],
  [/^(POST|PUT) \/transportation/, 'حفظ بيانات مواصلات'],
  [/^POST \/sections/, 'إضافة شعبة'],
  [/^PATCH \/sections/, 'تعديل شعبة'],
  [/^POST \/users\/\d+\/lock/, 'إيقاف حساب'],
];

export function describe(method, path) {
  const sig = `${method} ${path.split('?')[0]}`;
  const hit = LABELS.find(([re]) => re.test(sig));
  return hit ? hit[1] : 'حفظ تغييرات';
}

/** Register a waiting save. Returns its entry; `entry.cancelled` rejects
 *  when the user gives up on it from the pending list. */
export function enqueue(id, method, path) {
  if (items.has(id)) return items.get(id);
  let cancel;
  const cancelled = new Promise((_, reject) => { cancel = reject; });
  cancelled.catch(() => {}); // only observed while racing
  const entry = { id, method, path, label: describe(method, path), since: Date.now(), attempts: 0, cancelled, cancel };
  items.set(id, entry);
  publish();
  return entry;
}

export function attempt(id) {
  const e = items.get(id);
  if (e) { e.attempts += 1; publish(); }
}

/** The save reached the server (whatever it answered). */
export function settle(id) {
  if (!items.delete(id)) return;
  sentAfterWait += 1;
  publish();
}

export function cancelPending(id) {
  const e = items.get(id);
  if (!e) return;
  items.delete(id);
  const err = new Error('أُلغي الحفظ — لم يُرسل التغيير');
  err.code = 'CANCELLED';
  e.cancel(err);
  publish();
}

/** How many saves went out after waiting, since the last call. */
export function takeSentCount() {
  const n = sentAfterWait;
  sentAfterWait = 0;
  return n;
}

export function noteSnapshotServed(at) {
  if (!snapshotAt || at < snapshotAt) { snapshotAt = at; publish(); }
}
export function clearSnapshotServed() {
  if (snapshotAt) { snapshotAt = null; publish(); }
}

export function pendingCount() { return items.size; }

// Closing the tab would throw the waiting saves away — ask first.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', (e) => {
    if (!items.size) return;
    e.preventDefault();
    e.returnValue = '';
  });
}

/** { pending: entry[], snapshotAt } — re-renders on every change. */
export function useOutbox() {
  const read = () => ({ pending: [...items.values()], snapshotAt });
  const [value, setValue] = useState(read);
  useEffect(() => {
    const sync = () => setValue(read());
    listeners.add(sync);
    sync();
    return () => listeners.delete(sync);
  }, []);
  return value;
}

export function subscribeOutbox(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
